import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, ReactNode, useRef } from 'react';
import { useStore } from './StoreContext';
import { useAuth } from './AuthContext';
import roleService from '../services/role.service';
import customFieldService from '../services/customField.service';
import orderStatusService from '../services/orderStatus.service';
import storeService from '../services/store.service';
import integrationService from '../services/integration.service';
import productService from '../services/product.service';
import savedSearchService from '../services/savedSearch.service';
import myCommissionsService from '../services/myCommissions.service';
import cacheService from '../utils/cache.service';
import { supportScopedCacheKey } from '../utils/superAdminSupportAccess';
import { mergeDeliveryStatusOptionsFromApi } from '../utils/deliveryStatusList.utils';
import { logProductsDiag } from '../utils/productsDebug';
import {
  loadOrderCityFieldMappings,
  CityFieldMappings,
  cityFieldMappingsHasCities
} from '../utils/orderCityFieldMappings';
import { CustomField } from '../types/customField.types';
import { OrderStatus } from '../types/orderStatus.types';
import { StoreIntegration } from '../types/integration.types';
import { Product } from '../types/product.types';

/**
 * Store Data Context
 * Centralizes and caches store-specific data that is needed across multiple pages
 * This prevents redundant API calls when navigating between pages
 * 
 * Features:
 * - Persistent cache with localStorage (TTL: 5 minutes)
 * - Multi-store cache support
 * - Priority loading (critical data first)
 * - Automatic cache invalidation
 */

interface StoreUser {
  id: number;
  email: string;
  pseudo?: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
}

interface FieldOptions {
  primary_product_names: Array<{ label: string; value: string }>;
  primary_product_variants: Array<{ label: string; value: string }>;
  delivery_statuses: Array<{ label: string; value: string }>;
  delivery_companies: Array<{ label: string; value: string }>;
  delivery_situations: Array<{ label: string; value: string }>;
  assigned_to_pseudos: Array<{ label: string; value: string }>;
}

interface StoreDataContextType {
  // Data
  permissions: string[];
  customFields: CustomField[];
  orderStatuses: OrderStatus[];
  storeUsers: StoreUser[];
  integrations: StoreIntegration[];
  products: Product[];
  fieldOptions: FieldOptions;
  cityFieldMappings: CityFieldMappings;
  
  // Loading states
  isLoading: boolean;
  permissionsLoading: boolean;
  customFieldsLoading: boolean;
  orderStatusesLoading: boolean;
  storeUsersLoading: boolean;
  integrationsLoading: boolean;
  productsLoading: boolean;
  fieldOptionsLoading: boolean;
  cityFieldMappingsLoading: boolean;
  
  // Refresh functions (for manual reload if needed)
  refreshPermissions: (forceRefresh?: boolean) => Promise<void>;
  refreshCustomFields: (forceRefresh?: boolean) => Promise<void>;
  refreshOrderStatuses: () => Promise<void>;
  refreshStoreUsers: () => Promise<void>;
  refreshIntegrations: () => Promise<void>;
  refreshProducts: () => Promise<void>;
  refreshFieldOptions: () => Promise<void>;
  refreshCityFieldMappings: (forceRefresh?: boolean) => Promise<void>;
  refreshAll: () => Promise<void>;
  ensureIntegrations: () => Promise<void>;
  ensureProducts: () => Promise<void>;
  ensureFieldOptions: () => Promise<void>;
  getProductDetail: (productId: number, options?: { requireVariants?: boolean; forceRefresh?: boolean }) => Promise<Product | null>;
  invalidateProductsCache: () => void;
  refreshProductsAfterProductMutation: () => Promise<void>;
  
  // Commission tab visibility (only true when user has at least one commission)
  hasAnyCommission: boolean | null; // null = not loaded, false = no commission, true = has at least one
  hasAnyCommissionLoading: boolean;
  refreshHasAnyCommission: (forceRefresh?: boolean) => Promise<void>;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isAdmin: () => boolean;
  isOwner: () => boolean;
}

const StoreDataContext = createContext<StoreDataContextType | undefined>(undefined);

interface StoreDataProviderProps {
  children: ReactNode;
}

// Cache TTL configuration (en millisecondes)
// Défini en dehors du composant pour éviter les warnings ESLint
const SESSION_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h ; invalidation au logout uniquement

const CACHE_TTL = {
  permissions: SESSION_CACHE_TTL_MS,
  customFields: SESSION_CACHE_TTL_MS,
  orderStatuses: SESSION_CACHE_TTL_MS,
  storeUsers: SESSION_CACHE_TTL_MS,
  integrations: SESSION_CACHE_TTL_MS,
  products: SESSION_CACHE_TTL_MS,
  fieldOptions: SESSION_CACHE_TTL_MS,
  cityFieldMappings: SESSION_CACHE_TTL_MS,
  hasAnyCommission: SESSION_CACHE_TTL_MS
} as const;

export const StoreDataProvider: React.FC<StoreDataProviderProps> = ({ children }) => {
  const { currentStore } = useStore();
  const { user } = useAuth();
  
  // Data state
  const [permissions, setPermissions] = useState<string[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<OrderStatus[]>([]);
  const [storeUsers, setStoreUsers] = useState<StoreUser[]>([]);
  const [integrations, setIntegrations] = useState<StoreIntegration[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [fieldOptions, setFieldOptions] = useState<FieldOptions>({
    primary_product_names: [],
    primary_product_variants: [],
    delivery_statuses: [],
    delivery_companies: [],
    delivery_situations: [],
    assigned_to_pseudos: []
  });
  
  const [cityFieldMappings, setCityFieldMappings] = useState<CityFieldMappings>({});

  // Loading states
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(true);
  const [orderStatusesLoading, setOrderStatusesLoading] = useState(true);
  const [storeUsersLoading, setStoreUsersLoading] = useState(true);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [fieldOptionsLoading, setFieldOptionsLoading] = useState(false);
  const [cityFieldMappingsLoading, setCityFieldMappingsLoading] = useState(false);
  const [hasAnyCommission, setHasAnyCommission] = useState<boolean | null>(null);
  const [hasAnyCommissionLoading, setHasAnyCommissionLoading] = useState(false);

  // Track last loaded store/user to prevent duplicate loads
  const lastLoadedStoreRef = useRef<number | null>(null);
  const lastLoadedUserRef = useRef<number | null>(null);
  const isLoadingRef = useRef(false);
  const integrationsLoadedRef = useRef<Record<number, boolean>>({});
  const productsLoadedRef = useRef<Record<number, boolean>>({});
  const fieldOptionsLoadedRef = useRef<Record<number, boolean>>({});
  const productsFetchInProgressRef = useRef<boolean>(false);
  const integrationsFetchInProgressRef = useRef<boolean>(false);
  const productDetailsByStoreRef = useRef<Record<number, Map<number, Product>>>({});
  const productDetailInFlightRef = useRef<Map<string, Promise<Product | null>>>(new Map());
  const productDetailFetchedRef = useRef<Set<string>>(new Set());
  const productsRef = useRef(products);
  productsRef.current = products;

  // Refresh functions with cache support
  const refreshPermissions = useCallback(async (forceRefresh = false) => {
    if (!currentStore || !user) {
      setPermissions([]);
      setPermissionsLoading(false);
      return;
    }
    
    const permissionsCacheKey = supportScopedCacheKey(`permissions_user_${user.id}`);

    const cached = cacheService.get<string[]>(currentStore.id, permissionsCacheKey);
    const hasCached = Array.isArray(cached) && cached.length > 0;

    if (!forceRefresh && hasCached) {
      setPermissions(cached);
      setPermissionsLoading(false);
      return;
    }

    try {
      if (!hasCached) {
        setPermissionsLoading(true);
      }
      const userPermissions = await roleService.getUserPermissions(currentStore.id, user.id);
      setPermissions(userPermissions);
      cacheService.set(currentStore.id, permissionsCacheKey, userPermissions, CACHE_TTL.permissions);
    } catch (error) {
      console.error('[StoreDataContext] Failed to load permissions:', error);
      if (!hasCached) {
        setPermissions([]);
      }
    } finally {
      setPermissionsLoading(false);
    }
  }, [currentStore, user]);

  const refreshHasAnyCommission = useCallback(async (forceRefresh = false) => {
    if (!currentStore || !user) {
      setHasAnyCommission(null);
      setHasAnyCommissionLoading(false);
      return;
    }
    const cacheKey = supportScopedCacheKey(`commissions_has_any_user_${user.id}`);
    const cached = cacheService.get<boolean>(currentStore.id, cacheKey);
    const hasCached = cached !== undefined && cached !== null;

    if (!forceRefresh && hasCached) {
      setHasAnyCommission(cached);
      setHasAnyCommissionLoading(false);
      return;
    }

    try {
      if (!hasCached) {
        setHasAnyCommissionLoading(true);
      } else {
        setHasAnyCommission(cached);
      }
      const hasAny = await myCommissionsService.getHasAnyCommission(currentStore.id);
      setHasAnyCommission(hasAny);
      cacheService.set(currentStore.id, cacheKey, hasAny, CACHE_TTL.hasAnyCommission);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // 403 = pas le droit d'accéder aux commissions (backend) → masquer l'onglet
      if (status === 403) {
        setHasAnyCommission(false);
        cacheService.set(currentStore.id, cacheKey, false, CACHE_TTL.hasAnyCommission);
      } else if (!hasCached) {
        setHasAnyCommission(null);
      }
    } finally {
      setHasAnyCommissionLoading(false);
    }
  }, [currentStore, user]);

  const refreshCustomFields = useCallback(async (forceRefresh = false) => {
    if (!currentStore) {
      setCustomFields([]);
      setCustomFieldsLoading(false);
      return;
    }
    
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cacheService.get<CustomField[]>(currentStore.id, 'customFields');
      if (cached) {
        setCustomFields(cached);
        setCustomFieldsLoading(false);
        return;
      }
    }
    
    try {
      setCustomFieldsLoading(true);
      const fields = await customFieldService.getStoreFields(currentStore.id, { is_active: true });
      setCustomFields(fields);
      // Save to cache
      cacheService.set(currentStore.id, 'customFields', fields, CACHE_TTL.customFields);
    } catch (error) {
      console.error('[StoreDataContext] Failed to load custom fields:', error);
      setCustomFields([]);
    } finally {
      setCustomFieldsLoading(false);
    }
  }, [currentStore]);

  const refreshOrderStatuses = useCallback(async (forceRefresh = false) => {
    if (!currentStore) {
      setOrderStatuses([]);
      setOrderStatusesLoading(false);
      return;
    }
    
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cacheService.get<OrderStatus[]>(currentStore.id, 'orderStatuses');
      if (cached) {
        setOrderStatuses(cached);
        setOrderStatusesLoading(false);
        return;
      }
    }
    
    try {
      setOrderStatusesLoading(true);
      const statuses = await orderStatusService.getStoreStatuses(currentStore.id, { is_active: true });
      setOrderStatuses(statuses);
      // Save to cache
      cacheService.set(currentStore.id, 'orderStatuses', statuses, CACHE_TTL.orderStatuses);
    } catch (error) {
      console.error('[StoreDataContext] Failed to load order statuses:', error);
      setOrderStatuses([]);
    } finally {
      setOrderStatusesLoading(false);
    }
  }, [currentStore]);

  const refreshStoreUsers = useCallback(async (forceRefresh = false) => {
    if (!currentStore) {
      setStoreUsers([]);
      setStoreUsersLoading(false);
      return;
    }
    
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cacheService.get<StoreUser[]>(currentStore.id, 'storeUsers');
      if (cached) {
        setStoreUsers(cached);
        setStoreUsersLoading(false);
        return;
      }
    }
    
    try {
      setStoreUsersLoading(true);
      const response = await storeService.getStoreUsers(currentStore.id);
      if (response.success && response.data?.users) {
        setStoreUsers(response.data.users);
        // Save to cache
        cacheService.set(currentStore.id, 'storeUsers', response.data.users, CACHE_TTL.storeUsers);
      } else {
        setStoreUsers([]);
      }
    } catch (error) {
      console.error('[StoreDataContext] Failed to load store users:', error);
      setStoreUsers([]);
    } finally {
      setStoreUsersLoading(false);
    }
  }, [currentStore]);

  const refreshIntegrations = useCallback(async (forceRefresh = false) => {
    if (!currentStore) {
      setIntegrations([]);
      setIntegrationsLoading(false);
      return;
    }
    const storeId = currentStore.id;

    // Integrations list requires MANAGE_STORE on backend; avoid retry loop for users without it
    if (!permissions.includes('MANAGE_STORE')) {
      setIntegrations([]);
      setIntegrationsLoading(false);
      integrationsLoadedRef.current[storeId] = true;
      return;
    }
    
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cacheService.get<StoreIntegration[]>(currentStore.id, 'integrations');
      if (cached) {
        setIntegrations(cached);
        setIntegrationsLoading(false);
        integrationsLoadedRef.current[storeId] = true;
        return;
      }
    }

    if (integrationsFetchInProgressRef.current) return;
    integrationsFetchInProgressRef.current = true;
    
    try {
      setIntegrationsLoading(true);
      const storeIntegrations = await integrationService.getStoreIntegrations(currentStore.id);
      setIntegrations(storeIntegrations);
      // Save to cache
      cacheService.set(currentStore.id, 'integrations', storeIntegrations, CACHE_TTL.integrations);
      integrationsLoadedRef.current[storeId] = true;
    } catch (error: any) {
      console.error('[StoreDataContext] Failed to load integrations:', error);
      setIntegrations([]);
      if (error?.response?.status === 403) {
        integrationsLoadedRef.current[storeId] = true;
      }
    } finally {
      setIntegrationsLoading(false);
      integrationsFetchInProgressRef.current = false;
    }
  }, [currentStore, permissions]);

  const refreshCityFieldMappings = useCallback(async (forceRefresh = false) => {
    if (!currentStore) {
      setCityFieldMappings({});
      setCityFieldMappingsLoading(false);
      return;
    }

    if (!forceRefresh) {
      const cached = cacheService.get<CityFieldMappings>(currentStore.id, 'cityFieldMappings');
      if (cached && cityFieldMappingsHasCities(cached)) {
        setCityFieldMappings(cached);
        setCityFieldMappingsLoading(false);
        return;
      }
    }

    // Use customFields from state; if not yet propagated, fall back to localStorage cache
    const fieldsToUse = customFields.length > 0
      ? customFields
      : (cacheService.get<CustomField[]>(currentStore.id, 'customFields') ?? []);

    if (fieldsToUse.length === 0) {
      setCityFieldMappingsLoading(false);
      return;
    }

    try {
      setCityFieldMappingsLoading(true);
      const mappings = await loadOrderCityFieldMappings(currentStore.id, fieldsToUse);
      setCityFieldMappings(mappings);
      if (cityFieldMappingsHasCities(mappings)) {
        cacheService.set(currentStore.id, 'cityFieldMappings', mappings, CACHE_TTL.cityFieldMappings);
      } else {
        cacheService.remove(currentStore.id, 'cityFieldMappings');
      }
    } catch (error) {
      console.error('[StoreDataContext] Failed to load city field mappings:', error);
      setCityFieldMappings({});
    } finally {
      setCityFieldMappingsLoading(false);
    }
  }, [currentStore, customFields]);

  const refreshProducts = useCallback(async (forceRefresh = false) => {
    if (!currentStore) {
      logProductsDiag('refreshProducts: pas de boutique', {});
      setProducts([]);
      setProductsLoading(false);
      return;
    }
    const storeId = currentStore.id;

    logProductsDiag('refreshProducts entrée', {
      storeId,
      forceRefresh,
      dejaCharge: !!productsLoadedRef.current[storeId],
      fetchEnCours: productsFetchInProgressRef.current
    });

    if (!forceRefresh && productsLoadedRef.current[storeId]) {
      logProductsDiag('refreshProducts: sortie (déjà chargé)', { storeId });
      return;
    }
    
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cacheService.get<Product[]>(currentStore.id, 'products');
      if (cached) {
        logProductsDiag('refreshProducts: cache hit, pas de HTTP', { storeId, count: cached.length });
        setProducts(cached);
        setProductsLoading(false);
        productsLoadedRef.current[storeId] = true;
        return;
      }
    }

    if (productsFetchInProgressRef.current) {
      logProductsDiag('refreshProducts: sortie (fetch déjà en cours)', { storeId });
      return;
    }
    productsFetchInProgressRef.current = true;
    
    try {
      setProductsLoading(true);
      logProductsDiag('refreshProducts: appel HTTP liste (is_active)', { storeId });
      const result = await productService.getProducts(currentStore.id, { is_active: true }, 1, 100);
      const productsData = result.data || [];
      setProducts(productsData);
      if (!productDetailsByStoreRef.current[storeId]) {
        productDetailsByStoreRef.current[storeId] = new Map<number, Product>();
      }
      productsData.forEach((p: Product) => {
        productDetailsByStoreRef.current[storeId].set(p.id, p);
      });
      // Save to cache
      cacheService.set(currentStore.id, 'products', productsData, CACHE_TTL.products);
      productsLoadedRef.current[storeId] = true;
      logProductsDiag('refreshProducts: HTTP terminé', { storeId, count: productsData.length });
    } catch (error) {
      console.error('[StoreDataContext] Failed to load products:', error);
      setProducts([]);
    } finally {
      setProductsLoading(false);
      productsFetchInProgressRef.current = false;
    }
  }, [currentStore?.id]);

  const getProductDetail = useCallback(async (
    productId: number,
    options: { requireVariants?: boolean; forceRefresh?: boolean } = {}
  ): Promise<Product | null> => {
    if (!currentStore || !productId) return null;
    const { requireVariants = true, forceRefresh = false } = options;
    const storeId = currentStore.id;
    const detailKey = `${storeId}:${productId}`;
    if (!productDetailsByStoreRef.current[storeId]) {
      productDetailsByStoreRef.current[storeId] = new Map<number, Product>();
    }

    const hasUsableVariants = (product?: Product | null) =>
      !!(product?.variant_options && product.variant_options.length > 0);
    const knownWithoutVariants = (product?: Product | null) =>
      Number(product?.variant_options_count) === 0;

    const fromDetailCache = productDetailsByStoreRef.current[storeId].get(productId);
    if (fromDetailCache && !forceRefresh) {
      if (
        !requireVariants ||
        hasUsableVariants(fromDetailCache) ||
        knownWithoutVariants(fromDetailCache) ||
        productDetailFetchedRef.current.has(detailKey)
      ) {
        return fromDetailCache;
      }
    }

    if (!forceRefresh) {
      const fromProductsList = productsRef.current.find((p) => p.id === productId);
      if (fromProductsList) {
        productDetailsByStoreRef.current[storeId].set(productId, fromProductsList);
        if (!requireVariants || hasUsableVariants(fromProductsList) || knownWithoutVariants(fromProductsList)) {
          return fromProductsList;
        }
      }
    }

    const inFlight = productDetailInFlightRef.current.get(detailKey);
    if (inFlight) {
      return inFlight;
    }

    const request = productService.getProductById(storeId, productId)
      .then((fullProduct) => {
        productDetailFetchedRef.current.add(detailKey);
        productDetailsByStoreRef.current[storeId].set(productId, fullProduct);
        setProducts(prev => {
          const idx = prev.findIndex(p => p.id === productId);
          if (idx === -1) return prev;
          const current = prev[idx];
          const currentLen = current.variant_options?.length ?? -1;
          const nextLen = fullProduct.variant_options?.length ?? -1;
          if (
            currentLen === nextLen &&
            Number(current.variant_options_count) === Number(fullProduct.variant_options_count) &&
            current.price === fullProduct.price &&
            current.name === fullProduct.name
          ) {
            return prev;
          }
          const next = [...prev];
          next[idx] = { ...current, ...fullProduct };
          cacheService.set(storeId, 'products', next, CACHE_TTL.products);
          return next;
        });
        return fullProduct;
      })
      .catch(() => null)
      .finally(() => {
        productDetailInFlightRef.current.delete(detailKey);
      });

    productDetailInFlightRef.current.set(detailKey, request);
    return request;
  }, [currentStore?.id]);

  const invalidateProductsCache = useCallback(() => {
    if (!currentStore) return;
    const storeId = currentStore.id;
    cacheService.remove(storeId, 'products');
    productsLoadedRef.current[storeId] = false;
    productsFetchInProgressRef.current = false;
    productDetailsByStoreRef.current[storeId] = new Map<number, Product>();
    Array.from(productDetailFetchedRef.current).forEach((key) => {
      if (key.startsWith(`${storeId}:`)) productDetailFetchedRef.current.delete(key);
    });
  }, [currentStore?.id]);

  const refreshProductsAfterProductMutation = useCallback(async () => {
    invalidateProductsCache();
    await refreshProducts(true);
  }, [invalidateProductsCache, refreshProducts]);

  const refreshFieldOptions = useCallback(async (forceRefresh = false) => {
    if (!currentStore) {
      setFieldOptions({
        primary_product_names: [],
        primary_product_variants: [],
        delivery_statuses: [],
        delivery_companies: [],
        delivery_situations: [],
        assigned_to_pseudos: []
      });
      setFieldOptionsLoading(false);
      return;
    }
    const storeId = currentStore.id;
    
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cacheService.get<FieldOptions>(currentStore.id, 'fieldOptions');
      if (cached) {
        setFieldOptions(cached);
        setFieldOptionsLoading(false);
        fieldOptionsLoadedRef.current[storeId] = true;
        return;
      }
    }
    
    try {
      setFieldOptionsLoading(true);
      
      // Load all field options in parallel (one request for all delivery options)
      const [
        productNames,
        productVariants,
        deliveryOptions,
        assignedToPseudos
      ] = await Promise.all([
        savedSearchService.getFieldOptions(currentStore.id, 'primary_product_name', { limit: 200 }).catch(() => []),
        savedSearchService.getFieldOptions(currentStore.id, 'primary_product_variant', { limit: 200 }).catch(() => []),
        savedSearchService.getDeliveryFieldOptions(currentStore.id, { limit: 100 }).catch(() => ({
          delivery_statuses: [],
          delivery_companies: [],
          delivery_situations: []
        })),
        savedSearchService.getFieldOptions(currentStore.id, 'assigned_to_pseudo', { limit: 100 }).catch(() => [])
      ]);

      const options: FieldOptions = {
        primary_product_names: productNames,
        primary_product_variants: productVariants,
        delivery_statuses: mergeDeliveryStatusOptionsFromApi(deliveryOptions.delivery_statuses),
        delivery_companies: deliveryOptions.delivery_companies,
        delivery_situations: deliveryOptions.delivery_situations,
        assigned_to_pseudos: assignedToPseudos
      };
      
      setFieldOptions(options);
      // Save to cache
      cacheService.set(currentStore.id, 'fieldOptions', options, CACHE_TTL.fieldOptions);
      fieldOptionsLoadedRef.current[storeId] = true;
    } catch (error) {
      console.error('[StoreDataContext] Failed to load field options:', error);
      setFieldOptions({
        primary_product_names: [],
        primary_product_variants: [],
        delivery_statuses: [],
        delivery_companies: [],
        delivery_situations: [],
        assigned_to_pseudos: []
      });
    } finally {
      setFieldOptionsLoading(false);
    }
  }, [currentStore]);

  const ensureIntegrations = useCallback(async () => {
    if (!currentStore) return;
    const storeId = currentStore.id;
    if (integrationsLoadedRef.current[storeId] || integrationsFetchInProgressRef.current) return;
    await refreshIntegrations(false);
  }, [currentStore?.id, refreshIntegrations]);

  const ensureProducts = useCallback(async () => {
    if (!currentStore) return;
    const storeId = currentStore.id;
    if (productsLoadedRef.current[storeId] || productsFetchInProgressRef.current) {
      logProductsDiag('ensureProducts: rien à faire', {
        storeId,
        dejaCharge: !!productsLoadedRef.current[storeId],
        fetchEnCours: productsFetchInProgressRef.current
      });
      return;
    }
    logProductsDiag('ensureProducts → refreshProducts', { storeId });
    await refreshProducts(false);
  }, [currentStore?.id, refreshProducts]);

  const ensureFieldOptions = useCallback(async () => {
    if (!currentStore) return;
    const storeId = currentStore.id;
    if (fieldOptionsLoading || fieldOptionsLoadedRef.current[storeId]) return;
    await refreshFieldOptions(false);
  }, [currentStore, fieldOptionsLoading, refreshFieldOptions]);

  const refreshAll = useCallback(async (forceRefresh = false) => {
    if (!currentStore || !user) return;
    
    console.log(`[StoreDataContext] Loading all store data for store: ${currentStore.id}${forceRefresh ? ' (force refresh)' : ''}`);
    
    // Phase 1: Load critical data first (permissions, customFields, orderStatuses)
    // These are needed immediately for most pages
    const criticalPromises = [
      refreshPermissions(forceRefresh),
      refreshCustomFields(forceRefresh),
      refreshOrderStatuses(forceRefresh)
    ];
    
    await Promise.all(criticalPromises);
    
    // Phase 2: Load other data in parallel (can be loaded after critical data)
    const otherPromises = [
      refreshStoreUsers(forceRefresh),
      refreshIntegrations(forceRefresh),
      refreshProducts(forceRefresh)
      // Note: refreshFieldOptions(forceRefresh) was removed to prevent network stalling on initial load.
      // It should be loaded lazily via ensureFieldOptions() when actually needed by advanced search UI.
    ];
    
    await Promise.all(otherPromises);

    // Cities depend on customFields being loaded (Phase 1), so loaded after Phase 2
    await refreshCityFieldMappings(forceRefresh);
    
    console.log('[StoreDataContext] ✅ All store data loaded');
  }, [currentStore, user, refreshPermissions, refreshCustomFields, refreshOrderStatuses, refreshStoreUsers, refreshIntegrations, refreshProducts, refreshFieldOptions, refreshCityFieldMappings]);

  // Chargement initial au changement de boutique / utilisateur (refreshProducts absent du corps : pas en deps).
  useEffect(() => {
    if (!currentStore || !user) {
      logProductsDiag('effet init boutique: reset (pas de store ou user)', {});
      // Reset all data when no store
      setPermissions([]);
      setCustomFields([]);
      setOrderStatuses([]);
      setStoreUsers([]);
      setIntegrations([]);
      setProducts([]);
      setFieldOptions({
        primary_product_names: [],
        primary_product_variants: [],
        delivery_statuses: [],
        delivery_companies: [],
        delivery_situations: [],
        assigned_to_pseudos: []
      });
      setIntegrationsLoading(false);
      setProductsLoading(false);
      setFieldOptionsLoading(false);
      setCityFieldMappings({});
      setCityFieldMappingsLoading(false);
      setHasAnyCommission(null);
      lastLoadedStoreRef.current = null;
      lastLoadedUserRef.current = null;
      integrationsLoadedRef.current = {};
      productsLoadedRef.current = {};
      fieldOptionsLoadedRef.current = {};
      productsFetchInProgressRef.current = false;
      integrationsFetchInProgressRef.current = false;
      productDetailsByStoreRef.current = {};
      productDetailInFlightRef.current.clear();
      productDetailFetchedRef.current.clear();
      return;
    }
    
    // Skip if already loaded for this store and user, or currently loading
    if (
      (lastLoadedStoreRef.current === currentStore.id && lastLoadedUserRef.current === user.id) ||
      isLoadingRef.current
    ) {
      logProductsDiag('effet init boutique: ignoré (déjà chargé ou chargement en cours)', {
        storeId: currentStore.id,
        userId: user.id,
        dejaMemeCouple:
          lastLoadedStoreRef.current === currentStore.id && lastLoadedUserRef.current === user.id,
        isLoadingRef: isLoadingRef.current
      });
      return;
    }

    const previousStoreId = lastLoadedStoreRef.current;
    if (previousStoreId != null && previousStoreId !== currentStore.id) {
      cacheService.remove(previousStoreId, 'fieldOptions');
    }

    logProductsDiag('effet init boutique: nouveau cycle (refs produits remises à zéro pour ce store)', {
      storeId: currentStore.id,
      userId: user.id,
      previousStoreId
    });

    isLoadingRef.current = true;
    lastLoadedStoreRef.current = currentStore.id;
    lastLoadedUserRef.current = user.id;
    integrationsLoadedRef.current[currentStore.id] = false;
    productsLoadedRef.current[currentStore.id] = false;
    fieldOptionsLoadedRef.current[currentStore.id] = false;
    productsFetchInProgressRef.current = false;
    integrationsFetchInProgressRef.current = false;

    const loadFromCache = () => {
      // Load critical data from cache immediately (synchronous)
      const permissionsCacheKey = supportScopedCacheKey(`permissions_user_${user.id}`);
      const cachedPermissions = cacheService.get<string[]>(currentStore.id, permissionsCacheKey);
      const cachedCustomFields = cacheService.get<CustomField[]>(currentStore.id, 'customFields');
      const cachedOrderStatuses = cacheService.get<OrderStatus[]>(currentStore.id, 'orderStatuses');
      
      if (cachedPermissions && cachedPermissions.length > 0) {
        setPermissions(cachedPermissions);
        setPermissionsLoading(false);
      } else {
        setPermissions([]);
        setPermissionsLoading(true);
      }
      if (cachedCustomFields) setCustomFields(cachedCustomFields);
      if (cachedOrderStatuses) setOrderStatuses(cachedOrderStatuses);
      
      // Load other data from cache
      const cachedStoreUsers = cacheService.get<StoreUser[]>(currentStore.id, 'storeUsers');
      const cachedIntegrations = cacheService.get<StoreIntegration[]>(currentStore.id, 'integrations');
      const cachedProducts = cacheService.get<Product[]>(currentStore.id, 'products');
      const cachedFieldOptions = cacheService.get<FieldOptions>(currentStore.id, 'fieldOptions');
      const cachedHasAnyCommission = cacheService.get<boolean>(currentStore.id, supportScopedCacheKey(`commissions_has_any_user_${user.id}`));
      
      if (cachedStoreUsers) setStoreUsers(cachedStoreUsers);
      if (cachedIntegrations) {
        setIntegrations(cachedIntegrations);
        integrationsLoadedRef.current[currentStore.id] = true;
      }
      if (cachedProducts) {
        setProducts(cachedProducts);
        productsLoadedRef.current[currentStore.id] = true;
        if (!productDetailsByStoreRef.current[currentStore.id]) {
          productDetailsByStoreRef.current[currentStore.id] = new Map<number, Product>();
        }
        cachedProducts.forEach((p: Product) => {
          productDetailsByStoreRef.current[currentStore.id].set(p.id, p);
        });
      }
      if (cachedFieldOptions) {
        setFieldOptions(cachedFieldOptions);
        fieldOptionsLoadedRef.current[currentStore.id] = true;
      }
      if (cachedHasAnyCommission !== undefined && cachedHasAnyCommission !== null) {
        setHasAnyCommission(cachedHasAnyCommission);
      } else {
        setHasAnyCommission(null);
      }

      const cachedCityFieldMappings = cacheService.get<CityFieldMappings>(currentStore.id, 'cityFieldMappings');
      if (cachedCityFieldMappings && cityFieldMappingsHasCities(cachedCityFieldMappings)) {
        setCityFieldMappings(cachedCityFieldMappings);
      }
    };
    
    // Load from cache first (instant)
    loadFromCache();
    
    // Permissions: cache for first paint, then always re-fetch so role changes apply after reload
    Promise.resolve()
      .then(() => refreshPermissions(true))
      .then(() => Promise.all([
        refreshCustomFields(false),
        refreshOrderStatuses(false),
        refreshStoreUsers(false),
        refreshIntegrations(false)
      ]))
      .then(() => refreshCityFieldMappings(false))
      .finally(() => {
        isLoadingRef.current = false;
      });
  }, [
    currentStore,
    user,
    refreshPermissions,
    refreshCustomFields,
    refreshOrderStatuses,
    refreshStoreUsers,
    refreshIntegrations,
    refreshCityFieldMappings
  ]);

  // Appliquer le cache permissions avant le paint pour éviter le clignotement du nav.
  useLayoutEffect(() => {
    if (!currentStore || !user) {
      setPermissions([]);
      setPermissionsLoading(false);
      setHasAnyCommission(null);
      return;
    }
    const permissionsCacheKey = supportScopedCacheKey(`permissions_user_${user.id}`);
    const cachedPermissions = cacheService.get<string[]>(currentStore.id, permissionsCacheKey);
    if (cachedPermissions && cachedPermissions.length > 0) {
      setPermissions(cachedPermissions);
      setPermissionsLoading(false);
    } else {
      setPermissions([]);
      setPermissionsLoading(true);
    }
    const cachedHasAnyCommission = cacheService.get<boolean>(
      currentStore.id,
      supportScopedCacheKey(`commissions_has_any_user_${user.id}`)
    );
    if (cachedHasAnyCommission !== undefined && cachedHasAnyCommission !== null) {
      setHasAnyCommission(cachedHasAnyCommission);
    } else {
      setHasAnyCommission(null);
    }
  }, [currentStore?.id, user?.id]);

  // Vérifier côté API si l'utilisateur a au moins une commission (pour afficher l'onglet).
  useEffect(() => {
    if (!currentStore || !user || permissionsLoading) return;
    const canViewCommissions = permissions.includes('VIEW_COMMISSIONS') || permissions.includes('MANAGE_COMMISSIONS');
    if (!canViewCommissions) {
      setHasAnyCommission(false);
      return;
    }
    refreshHasAnyCommission(false);
  }, [
    currentStore?.id,
    user?.id,
    permissionsLoading,
    permissions,
    refreshHasAnyCommission
  ]);

  // Permission helper functions
  const hasPermission = useCallback((permission: string): boolean => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(permission => permissions.includes(permission));
  }, [permissions]);

  const hasAllPermissions = useCallback((requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(permission => permissions.includes(permission));
  }, [permissions]);

  const isAdmin = useCallback((): boolean => {
    const adminPermissions = [
      'MANAGE_ALL_ORDERS',
      'MANAGE_STORE_USERS',
      'MANAGE_ROLES',
      'MANAGE_STORE',
      'VIEW_STORE_LOGS',
      'MANAGE_CHART_MODELS',
      'TREATMENTS_VIEW'
    ];
    return hasAnyPermission(adminPermissions);
  }, [hasAnyPermission]);

  const isOwner = useCallback((): boolean => {
    return hasPermission('MANAGE_STORE') || 
           (hasPermission('MANAGE_ALL_ORDERS') && hasPermission('DISPATCH_ORDERS'));
  }, [hasPermission]);

  // Compute overall loading state
  const isLoading = permissionsLoading || customFieldsLoading || orderStatusesLoading || 
                    storeUsersLoading;

  const value: StoreDataContextType = {
    // Data
    permissions,
    customFields,
    orderStatuses,
    storeUsers,
    integrations,
    products,
    fieldOptions,
    cityFieldMappings,
    
    // Loading states
    isLoading,
    permissionsLoading,
    customFieldsLoading,
    orderStatusesLoading,
    storeUsersLoading,
    integrationsLoading,
    productsLoading,
    fieldOptionsLoading,
    cityFieldMappingsLoading,
    
    // Refresh functions
    refreshPermissions,
    refreshCustomFields,
    refreshOrderStatuses,
    refreshStoreUsers,
    refreshIntegrations,
    refreshProducts,
    refreshFieldOptions,
    refreshCityFieldMappings,
    refreshHasAnyCommission,
    refreshAll,
    ensureIntegrations,
    ensureProducts,
    ensureFieldOptions,
    getProductDetail,
    invalidateProductsCache,
    refreshProductsAfterProductMutation,
    
    // Commission tab visibility
    hasAnyCommission,
    hasAnyCommissionLoading,
    
    // Permission helpers
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isOwner
  };

  return <StoreDataContext.Provider value={value}>{children}</StoreDataContext.Provider>;
};

/**
 * Custom hook to use store data context
 * @returns StoreDataContextType
 */
export const useStoreData = (): StoreDataContextType => {
  const context = useContext(StoreDataContext);
  if (context === undefined) {
    throw new Error('useStoreData must be used within a StoreDataProvider');
  }
  return context;
};

export default StoreDataContext;

